"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.cleanUndefined = cleanUndefined;
exports.default = composeFromYaml;
const js_yaml_1 = __importDefault(require("js-yaml"));
/* -----------------------------
 * Normalizers & helpers
 * -----------------------------*/
function isObject(x) {
    return x && typeof x === 'object' && !Array.isArray(x);
}
function arrify(x) {
    if (x == null) {
        return undefined;
    }
    return Array.isArray(x) ? x : [x];
}
function normalizeStringMap(input) {
    if (!input) {
        return undefined;
    }
    if (Array.isArray(input)) {
        const out = {};
        for (const v of input) {
            const i = v.indexOf('=');
            if (i === -1) {
                out[v] = '';
            }
            else {
                out[v.slice(0, i)] = v.slice(i + 1);
            }
        }
        return out;
    }
    // force string values
    const out = {};
    for (const [k, v] of Object.entries(input)) {
        out[k] = String(v);
    }
    return out;
}
function normalizeEnvironment(env) {
    return normalizeStringMap(env);
}
function normalizeDependsOn(dep) {
    if (!dep) {
        return undefined;
    }
    if (Array.isArray(dep)) {
        return dep;
    }
    if (isObject(dep)) {
        return dep;
    }
    // string (legacy unlikely here, but normalize)
    return [String(dep)];
}
function normalizePorts(ports) {
    if (!ports) {
        return undefined;
    }
    return ports.map(p => {
        var _a;
        if (typeof p === 'string') {
            return p;
        }
        // ensure shape correctness
        const obj = {
            target: Number(p.target),
            published: p.published != null ? Number(p.published) : undefined,
            protocol: (_a = p.protocol) !== null && _a !== void 0 ? _a : 'tcp',
            mode: p.mode,
        };
        if (!obj.target || Number.isNaN(obj.target)) {
            throw new Error(`Invalid port target: ${JSON.stringify(p)}`);
        }
        return obj;
    });
}
function normalizeVolumes(vols) {
    if (!vols) {
        return undefined;
    }
    return vols.map(v => {
        if (typeof v === 'string') {
            return v;
        }
        const mv = { target: v.target };
        if (v.type) {
            mv.type = v.type;
        }
        if (v.source) {
            mv.source = v.source;
        }
        if (v.read_only != null) {
            mv.read_only = !!v.read_only;
        }
        if (v.consistency) {
            mv.consistency = v.consistency;
        }
        if (v.bind) {
            mv.bind = Object.assign({}, v.bind);
        }
        if (v.volume) {
            mv.volume = Object.assign({}, v.volume);
        }
        if (v.tmpfs) {
            mv.tmpfs = Object.assign({}, v.tmpfs);
        }
        return mv;
    });
}
function normalizeNetworks(n) {
    if (!n) {
        return undefined;
    }
    if (Array.isArray(n)) {
        return n.map(item => (typeof item === 'string' ? item : Object.assign({}, item)));
    }
    if (isObject(n)) {
        return Object.assign({}, n);
    } // StringMap form
    return undefined;
}
function normalizeLabels(labels) {
    // accept object or ["k=v"]
    if (!labels) {
        return undefined;
    }
    if (Array.isArray(labels)) {
        return labels;
    }
    const normalized = normalizeStringMap(labels);
    return normalized;
}
function normalizeHealthcheck(h) {
    if (!h) {
        return undefined;
    }
    if (!('test' in h)) {
        return undefined;
    }
    const test = Array.isArray(h.test) || typeof h.test === 'string' ? h.test : ['NONE'];
    const hc = { test };
    if (h.interval != null) {
        hc.interval = String(h.interval);
    }
    if (h.timeout != null) {
        hc.timeout = String(h.timeout);
    }
    if (h.retries != null) {
        hc.retries = Number(h.retries);
    }
    if (h.start_period != null) {
        hc.start_period = String(h.start_period);
    }
    return hc;
}
function normalizeBuild(b) {
    var _a;
    if (!b) {
        return undefined;
    }
    if (typeof b === 'string') {
        return b;
    } // shorthand -> context path
    const out = Object.assign({}, b);
    if (out.args) {
        // ensure args are KV (stringifiable)
        const kv = {};
        for (const [k, v] of Object.entries(out.args)) {
            kv[k] = typeof v === 'boolean' ? (v ? '1' : '0') : String(v);
        }
        out.args = kv;
    }
    if (out.labels) {
        out.labels = (_a = normalizeStringMap(out.labels)) !== null && _a !== void 0 ? _a : {};
    }
    if (out.extra_hosts && Array.isArray(out.extra_hosts)) {
        // keep as a list "host:ip" if provided; allowed by "compose"
        out.extra_hosts = out.extra_hosts.slice();
    }
    return out;
}
function cleanUndefined(obj) {
    if (typeof obj !== 'object' || obj === null) {
        return obj;
    }
    if (Array.isArray(obj)) {
        const res = [];
        for (let i = 0; i < obj.length; i++) {
            if (obj[i] !== undefined) {
                if (typeof obj[i] === 'object' && obj[i] !== null) {
                    const item = cleanUndefined(obj[i]);
                    if (item !== undefined &&
                        (typeof item !== 'object' ||
                            (Array.isArray(item) ? item.length > 0 : Object.keys(item).length > 0))) {
                        res.push(item);
                    }
                }
                else {
                    res.push(obj[i]);
                }
            }
        }
        if (!res.length) {
            return undefined;
        }
        return res.map(cleanUndefined);
    }
    const res = {};
    for (const [k, v] of Object.entries(obj)) {
        const item = cleanUndefined(v);
        if (item !== undefined) {
            res[k] = item;
        }
    }
    if (Object.keys(res).length === 0) {
        return undefined;
    }
    return res;
}
/* -----------------------------
 * Main converter
 * -----------------------------*/
function composeFromYaml(input) {
    var _a;
    const raw = typeof input === 'string' ? js_yaml_1.default.load(input) : input;
    if (!raw || typeof raw !== 'object') {
        throw new Error('Compose: cannot parse input');
    }
    const version = (_a = raw.version) !== null && _a !== void 0 ? _a : '3.9';
    const servicesIn = raw.services;
    if (!servicesIn || typeof servicesIn !== 'object') {
        throw new Error('Compose: missing `services`');
    }
    const services = {};
    for (const [name, svcRaw] of Object.entries(servicesIn)) {
        if (!isObject(svcRaw)) {
            throw new Error(`Compose: service ${name} must be an object`);
        }
        const s = {};
        s.container_name = svcRaw.container_name;
        s.image = svcRaw.image;
        s.build = normalizeBuild(svcRaw.build);
        s.command = svcRaw.command;
        s.entrypoint = svcRaw.entrypoint;
        s.user = svcRaw.user;
        s.working_dir = svcRaw.working_dir;
        s.hostname = svcRaw.hostname;
        s.domainname = svcRaw.domainname;
        s.environment = normalizeEnvironment(svcRaw.environment);
        s.env_file = svcRaw.env_file;
        s.labels = normalizeLabels(svcRaw.labels);
        s.ports = normalizePorts(arrify(svcRaw.ports));
        s.expose = arrify(svcRaw.expose);
        s.volumes = normalizeVolumes(arrify(svcRaw.volumes));
        s.devices = arrify(svcRaw.devices);
        if (svcRaw.extra_hosts) {
            s.extra_hosts = Array.isArray(svcRaw.extra_hosts) ? svcRaw.extra_hosts.slice() : Object.assign({}, svcRaw.extra_hosts);
        }
        s.dns = arrify(svcRaw.dns);
        s.dns_search = arrify(svcRaw.dns_search);
        s.dns_opt = arrify(svcRaw.dns_opt);
        s.networks = normalizeNetworks(svcRaw.networks);
        s.healthcheck = normalizeHealthcheck(svcRaw.healthcheck);
        s.restart = svcRaw.restart;
        s.tty = !!svcRaw.tty;
        s.stdin_open = !!svcRaw.stdin_open;
        // Normalize depends_on: accept array, map, or legacy boolean form
        // If someone used legacy `depends_on: { svc: { condition: service_healthy } }` we keep as-is.
        // If they used `depends_on: [svc1, svc2]`, keep as an array.
        s.depends_on = normalizeDependsOn(svcRaw.depends_on);
        if (svcRaw.stop_grace_period != null) {
            s.stop_grace_period = String(svcRaw.stop_grace_period);
        }
        if (svcRaw.stop_signal != null) {
            s.stop_signal = String(svcRaw.stop_signal);
        }
        if (svcRaw.logging) {
            const l = {};
            if (svcRaw.logging.driver) {
                l.driver = String(svcRaw.logging.driver);
            }
            if (svcRaw.logging.options) {
                const opt = {};
                for (const [k, v] of Object.entries(svcRaw.logging.options)) {
                    opt[k] = typeof v === 'boolean' ? (v ? '1' : '0') : String(v);
                }
                l.options = opt;
            }
            s.logging = l;
        }
        s.security_opt = arrify(svcRaw.security_opt);
        if (svcRaw.privileged != null) {
            s.privileged = !!svcRaw.privileged;
        }
        if (svcRaw.read_only != null) {
            s.read_only = !!svcRaw.read_only;
        }
        if (svcRaw.shm_size != null) {
            s.shm_size = svcRaw.shm_size;
        }
        if (svcRaw.sysctls) {
            s.sysctls = Object.assign({}, svcRaw.sysctls);
        }
        if (svcRaw.deploy) {
            // Keep deploy mostly as-is; Compose will validate at runtime.
            s.deploy = Object.assign({}, svcRaw.deploy);
        }
        // Preserve unknown extension fields under x_extra
        for (const [k, v] of Object.entries(svcRaw)) {
            if (k.startsWith('x-')) {
                s.x_extra = s.x_extra || {};
                s.x_extra[k] = v;
            }
        }
        services[name] = s;
    }
    // Top-level networks/volumes/secrets/configs: shallow copy/normalize label maps
    const networks = raw.networks ? Object.assign({}, raw.networks) : undefined;
    const volumes = raw.volumes ? Object.assign({}, raw.volumes) : undefined;
    const secrets = raw.secrets ? Object.assign({}, raw.secrets) : undefined;
    const configs = raw.configs ? Object.assign({}, raw.configs) : undefined;
    // Preserve top-level x-* extension fields
    const extra = {};
    for (const [k, v] of Object.entries(raw)) {
        if (k.startsWith('x-')) {
            extra[k] = v;
        }
    }
    const out = Object.assign({ version,
        services,
        networks,
        volumes,
        secrets,
        configs }, extra);
    return cleanUndefined(out);
}
